import { User } from "firebase/auth";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import ConfirmModal from "../../ConfirmModal";
import Modal from "../../Modal";
import {
  patchGameweek, addGameweeks, deleteGameweek, fetchAdminGameweekFixtures, fetchGameweekProposal,
  AdminCompetition, AdminFixture, GameweekFetchProposal,
} from "../../../api/admin";
import { card, primaryBtn, secondaryBtn, smallBtn, dangerBtn, inputStyle, badge, errorStyle, panelHeading } from "../../../styles";

interface Props {
  user: User;
  comp: AdminCompetition;
  reload: () => Promise<void>;
}

// Schedule list only — submitting/editing a gameweek's results, its fixtures, and
// its FPL proposal all live on the gameweek operations page now (one URL per GW,
// not a shared modal wizard). See GameweekOpsPage.tsx.
export default function GameweeksTab({ user, comp, reload }: Props) {
  const { t } = useTranslation();

  const [editingShortNameGwId, setEditingShortNameGwId] = useState<string | null>(null);
  const [editingShortNameValue, setEditingShortNameValue] = useState("");
  const [editingNameGwId, setEditingNameGwId] = useState<string | null>(null);
  const [editingNameValue, setEditingNameValue] = useState("");

  const [addCount, setAddCount] = useState(1);
  const [adding, setAdding] = useState(false);
  const [confirmDeleteGwId, setConfirmDeleteGwId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [manageScheduleOpen, setManageScheduleOpen] = useState(false);

  const [expandedFixturesGwId, setExpandedFixturesGwId] = useState<string | null>(null);
  const [fixturesByGwId, setFixturesByGwId] = useState<Record<string, AdminFixture[]>>({});
  const [proposalByGwId, setProposalByGwId] = useState<Record<string, GameweekFetchProposal | null>>({});
  const [fixturesLoading, setFixturesLoading] = useState(false);

  const toggleFixtures = async (gwId: string) => {
    if (expandedFixturesGwId === gwId) { setExpandedFixturesGwId(null); return; }
    setExpandedFixturesGwId(gwId);
    if (fixturesByGwId[gwId]) return;
    setFixturesLoading(true);
    try {
      const [{ fixtures }, proposal] = await Promise.all([
        fetchAdminGameweekFixtures(user, comp.id, gwId),
        fetchGameweekProposal(user, comp.id, gwId),
      ]);
      setFixturesByGwId((prev) => ({ ...prev, [gwId]: fixtures }));
      setProposalByGwId((prev) => ({ ...prev, [gwId]: proposal }));
    } catch {
      setError(t("admin.competitions.gwList.errors.failedToLoadFixtures"));
    } finally {
      setFixturesLoading(false);
    }
  };

  const saveShortName = async (gwId: string, value: string) => {
    const val = value.trim() || null;
    setEditingShortNameGwId(null);
    await patchGameweek(user, comp.id, gwId, { shortName: val });
    reload();
  };

  const saveName = async (gwId: string, value: string) => {
    const val = value.trim();
    setEditingNameGwId(null);
    if (!val) return;
    await patchGameweek(user, comp.id, gwId, { name: val });
    reload();
  };

  const handleAddGameweeks = async () => {
    setAdding(true);
    setError("");
    try {
      await addGameweeks(user, comp.id, addCount);
      setAddCount(1);
      await reload();
    } catch {
      setError(t("admin.competitions.gwList.errors.failedToAdd"));
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteGameweek = async () => {
    if (!confirmDeleteGwId) return;
    setDeleting(true);
    setError("");
    try {
      await deleteGameweek(user, comp.id, confirmDeleteGwId);
      setConfirmDeleteGwId(null);
      await reload();
    } catch {
      setError(t("admin.competitions.gwList.errors.failedToDelete"));
      setConfirmDeleteGwId(null);
    } finally {
      setDeleting(false);
    }
  };

  const realGws = comp.gameweeks.filter((gw) => gw.number > 0);
  const canEditShape = comp.status === "UPCOMING";
  const gwToDelete = comp.gameweeks.find((gw) => gw.id === confirmDeleteGwId);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      {error && <p style={errorStyle}>{error}</p>}

      {canEditShape && (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button style={secondaryBtn} onClick={() => setManageScheduleOpen(true)}>
            {t("admin.competitions.gwList.manageSchedule")}
          </button>
        </div>
      )}

      <div style={card}>
        {realGws.length === 0 ? (
          <p style={{ color: "var(--color-text-muted)", margin: 0 }}>{t("admin.competitions.gwList.title")}</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {realGws.map((gw) => (
              <div key={gw.id}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap", padding: "0.6rem 0.75rem", borderRadius: "10px", background: "var(--color-surface-alt)", border: "1px solid var(--color-border-subtle)" }}>
                <div style={{ flex: 1, minWidth: "140px" }}>
                  <input
                    value={editingNameGwId === gw.id ? editingNameValue : gw.name}
                    style={{
                      fontSize: "0.9rem", fontWeight: 600, padding: "3px 8px", borderRadius: "6px",
                      border: "1px solid var(--color-border)", background: "var(--color-surface)", color: "var(--color-text)",
                      width: "100%", boxSizing: "border-box", marginBottom: "0.4rem",
                    }}
                    onFocus={() => { setEditingNameGwId(gw.id); setEditingNameValue(gw.name); }}
                    onChange={(e) => setEditingNameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveName(gw.id, editingNameValue);
                      else if (e.key === "Escape") setEditingNameGwId(null);
                    }}
                    onBlur={() => { if (editingNameGwId === gw.id) saveName(gw.id, editingNameValue); }}
                  />
                  <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                    <label style={{ fontSize: "0.72rem", color: "var(--color-text-muted)", whiteSpace: "nowrap" }}>{t("admin.gameweekOps.shortNameLabel")}</label>
                    <input
                      value={editingShortNameGwId === gw.id ? editingShortNameValue : (gw.shortName ?? "")}
                      maxLength={8}
                      placeholder={t("admin.gameweekOps.shortNamePlaceholder")}
                      style={{ fontSize: "0.8rem", padding: "3px 8px", borderRadius: "6px", border: "1px solid var(--color-border)", background: "var(--color-surface)", color: "var(--color-text)", width: "80px" }}
                      onFocus={() => { setEditingShortNameGwId(gw.id); setEditingShortNameValue(gw.shortName ?? ""); }}
                      onChange={(e) => setEditingShortNameValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveShortName(gw.id, editingShortNameValue);
                        else if (e.key === "Escape") setEditingShortNameGwId(null);
                      }}
                      onBlur={() => { if (editingShortNameGwId === gw.id) saveShortName(gw.id, editingShortNameValue); }}
                    />
                  </div>
                </div>

                {gw.completedAt
                  ? <span style={{ ...badge, background: "var(--color-surface-alt)", color: "var(--color-text-muted)", border: "1px solid var(--color-border-subtle)" }}>{t("admin.competitions.gwList.completed")}</span>
                  : <span style={{ ...badge, background: "var(--color-status-active-bg)", color: "var(--color-status-active-text)" }}>{t("admin.competitions.gwList.open")}</span>}

                <button style={smallBtn} onClick={() => toggleFixtures(gw.id)}>
                  {expandedFixturesGwId === gw.id ? t("admin.competitions.gwList.hideFplData") : t("admin.competitions.gwList.showFplData")}
                </button>

                <Link to={`${gw.id}`} style={{ textDecoration: "none" }}>
                  <button style={gw.completedAt ? smallBtn : primaryBtn}>
                    {gw.completedAt ? t("admin.gameweekOps.editBtn") : t("admin.gameweekOps.openBtn")}
                  </button>
                </Link>
              </div>

              {expandedFixturesGwId === gw.id && (
                <div style={{ padding: "0.6rem 0.9rem", margin: "0.3rem 0 0", borderRadius: "10px", background: "var(--color-surface)", border: "1px solid var(--color-border-subtle)" }}>
                  {fixturesLoading && !fixturesByGwId[gw.id] ? (
                    <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--color-text-muted)" }}>{t("common.loading")}</p>
                  ) : (fixturesByGwId[gw.id]?.length ?? 0) === 0 ? (
                    <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--color-text-muted)" }}>{t("admin.competitions.gwList.noFixtures")}</p>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                      {fixturesByGwId[gw.id]!.map((f) => (
                        <div key={f.id} style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.82rem" }}>
                          <span style={{ flex: 1, textAlign: "right", minWidth: "80px", color: "var(--color-text)" }}>{f.homeClub.name}</span>
                          <span style={{ color: "var(--color-text-muted)", minWidth: "36px", textAlign: "center" }}>
                            {f.homeScore != null && f.awayScore != null ? `${f.homeScore} – ${f.awayScore}` : "v"}
                          </span>
                          <span style={{ flex: 1, minWidth: "80px", color: "var(--color-text)" }}>{f.awayClub.name}</span>
                          <span style={{ ...badge, background: "var(--color-surface-alt)", color: "var(--color-text-muted)", fontSize: "0.68rem" }}>{f.status}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <p style={{ margin: "0.75rem 0 0.35rem", fontSize: "0.72rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-dim)" }}>
                    {t("admin.competitions.gwList.fplDataLabel")}
                  </p>
                  {fixturesLoading && !(gw.id in proposalByGwId) ? (
                    <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--color-text-muted)" }}>{t("common.loading")}</p>
                  ) : !proposalByGwId[gw.id] ? (
                    <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--color-text-muted)" }}>{t("admin.competitions.gwList.noFplData")}</p>
                  ) : proposalByGwId[gw.id]!.status !== "READY" ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}>
                      {/* A stale non-READY status with no timestamp is indistinguishable from a
                          fresh one — this is what let GW1's proposal sit frozen unnoticed. */}
                      <p style={{ margin: 0, fontSize: "0.78rem", color: "var(--color-text-muted)" }}>
                        {t("admin.competitions.gwList.fetchedAt", { date: new Date(proposalByGwId[gw.id]!.fetchedAt).toLocaleString() })}
                      </p>
                      <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--color-warning)" }}>
                        {t("admin.competitions.gwList.fplDataStatus", { status: proposalByGwId[gw.id]!.status })}
                        {proposalByGwId[gw.id]!.errorMessage ? ` — ${proposalByGwId[gw.id]!.errorMessage}` : ""}
                      </p>
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                      <p style={{ margin: "0 0 0.2rem", fontSize: "0.78rem", color: "var(--color-text-muted)" }}>
                        {t("admin.competitions.gwList.fetchedAt", { date: new Date(proposalByGwId[gw.id]!.fetchedAt).toLocaleString() })}
                      </p>
                      {(proposalByGwId[gw.id] as Extract<GameweekFetchProposal, { status: "READY" }>).payload.stats.length === 0 ? (
                        <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--color-text-muted)" }}>{t("admin.competitions.gwList.noPlayerStats")}</p>
                      ) : (
                        (proposalByGwId[gw.id] as Extract<GameweekFetchProposal, { status: "READY" }>).payload.stats.map((s) => (
                          <div key={s.playerId} style={{ display: "flex", alignItems: "center", gap: "0.6rem", fontSize: "0.82rem" }}>
                            <span style={{ flex: 1, color: "var(--color-text)" }}>{s.playerName}</span>
                            <span style={{ color: "var(--color-text-muted)" }}>{t("admin.competitions.gwList.statsSummary", { goals: s.goals, assists: s.assists, yellow: s.yellowCards ?? 0, red: s.redCards ?? 0 })}</span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
              </div>
            ))}
          </div>
        )}
      </div>

      {manageScheduleOpen && (
        <Modal onClose={() => setManageScheduleOpen(false)} blur={false}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--color-surface)", borderRadius: "20px", border: "1px solid var(--color-border)", padding: "1.5rem", maxWidth: "440px", width: "90%" }}>
            <h3 style={{ marginTop: 0, ...panelHeading }}>{t("admin.competitions.gwList.manageSchedule")}</h3>

            <div style={{ marginBottom: "1.25rem" }}>
              <p style={{ margin: "0 0 0.6rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>{t("admin.competitions.gwList.addLabel")}</p>
              <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                <input
                  type="number"
                  min={1}
                  style={{ ...inputStyle, width: "100px" }}
                  value={addCount}
                  onChange={(e) => setAddCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
                />
                <button style={secondaryBtn} onClick={handleAddGameweeks} disabled={adding}>
                  {adding ? t("common.saving") : t("admin.competitions.gwList.addBtn")}
                </button>
              </div>
            </div>

            <div>
              <p style={{ margin: "0 0 0.6rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>{t("admin.competitions.gwList.removeLabel")}</p>
              {realGws.filter((gw) => !gw.completedAt).length === 0 ? (
                <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--color-text-dim)" }}>{t("admin.competitions.gwList.noneRemovable")}</p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                  {realGws.filter((gw) => !gw.completedAt).map((gw) => (
                    <div key={gw.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.5rem", fontSize: "0.85rem" }}>
                      <span>{gw.name}</span>
                      <button style={dangerBtn} onClick={() => setConfirmDeleteGwId(gw.id)}>{t("common.remove")}</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1.25rem" }}>
              <button style={secondaryBtn} onClick={() => setManageScheduleOpen(false)}>{t("common.close")}</button>
            </div>
          </div>
        </Modal>
      )}

      {confirmDeleteGwId && (
        <ConfirmModal
          title={t("admin.competitions.gwList.deleteConfirm.title")}
          message={t("admin.competitions.gwList.deleteConfirm.message", { name: gwToDelete?.name ?? "" })}
          confirmLabel={t("common.remove")}
          confirming={deleting}
          onConfirm={handleDeleteGameweek}
          onCancel={() => setConfirmDeleteGwId(null)}
        />
      )}
    </div>
  );
}
